/**
 * PGK launch counter (internal demo) — see snippets/pgk-launch-counter.liquid.
 *
 * The number shown is a PROJECTION derived from the time elapsed since the configured launch timestamp. It never
 * reads order data and nothing is incremented or stored server-side, so every visitor computes the same value.
 */
(function () {
  'use strict';

  if (window.__pgkLaunchCounter) return;
  window.__pgkLaunchCounter = true;

  var HOUR = 3600000;
  var INITIAL_COUNT = 2;
  var MAX_ANIMATED_GAP = 10;
  var MAX_TIMEOUT = 2147483647;
  var FONT_URL = 'https://fonts.googleapis.com/css2?family=Anton&display=swap';
  var BLOCKER_SELECTOR =
    'dialog[open], [role="dialog"][open], [role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]';

  var root = document.getElementById('pgk-launch-counter');
  var configElement = document.getElementById('pgk-launch-counter-config');
  if (!root || !configElement) return;

  var config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  /* ------------------------------------------------------------------------------------------------------------
   * Schedule
   * ---------------------------------------------------------------------------------------------------------- */

  // Accepts ISO 8601 with an explicit offset only (e.g. 2026-10-01T09:00:00+01:00). Anything else fails closed.
  function parseLaunch(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.exec(value || '');
    if (!match) return null;

    var time = Date.parse(value);
    if (isNaN(time)) return null;

    // Reject dates the parser would roll over (31 September, 25:00...)
    var wall = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0)));
    if (wall.getUTCMonth() !== +match[2] - 1 || wall.getUTCDate() !== +match[3] || wall.getUTCHours() !== +match[4] || wall.getUTCMinutes() !== +match[5]) {
      return null;
    }
    return time;
  }

  // projectedCount(t) from the brief (2 → 25 over hour one, 25 → 35 over hour two, then +1 every 20 minutes), with
  // the elapsed time kept in whole milliseconds so each step lands exactly on its boundary.
  function projectedCount(elapsed) {
    if (elapsed < 0) return null;
    if (elapsed < HOUR) return 2 + Math.floor((23 * elapsed) / HOUR);
    if (elapsed < 2 * HOUR) return 25 + Math.floor((10 * (elapsed - HOUR)) / HOUR);
    return 35 + Math.floor((3 * (elapsed - 2 * HOUR)) / HOUR);
  }

  // Inverse of projectedCount: milliseconds after launch at which the projection first reaches `count`.
  function elapsedToReach(count) {
    if (count <= 2) return 0;
    if (count <= 25) return Math.ceil(((count - 2) * HOUR) / 23);
    if (count <= 35) return HOUR + Math.ceil(((count - 25) * HOUR) / 10);
    return 2 * HOUR + Math.ceil(((count - 35) * HOUR) / 3);
  }

  function toNumber(value, fallback) {
    var number = typeof value === 'number' ? value : parseFloat(value);
    return isFinite(number) && number >= 0 ? number : fallback;
  }

  var launchTime = parseLaunch(config.launchAt);
  var threshold = toNumber(config.threshold, 20);
  var delay = toNumber(config.delay, 6) * 1000;

  function currentCount() {
    return projectedCount(Date.now() - launchTime);
  }

  /* ------------------------------------------------------------------------------------------------------------
   * Guards
   * ---------------------------------------------------------------------------------------------------------- */

  // The snippet is only output in the theme editor or by a non-published theme; this re-checks the same thing
  // against Shopify's own client-side theme data, so both signals have to agree.
  function isPreviewContext() {
    var shopify = window.Shopify || {};
    if (shopify.designMode === true) return true;
    var theme = shopify.theme;
    return !!theme && typeof theme.role === 'string' && theme.role !== 'main' && String(theme.id) === String(config.themeId);
  }

  function isExcludedPage() {
    if (/(^|\/)(cart|checkouts?)(\/|$)/.test(window.location.pathname)) return true;
    try {
      return String(window.ShopifyAnalytics.meta.product.id) === String(config.productId);
    } catch (error) {
      return false;
    }
  }

  function storageGet(storageName, key) {
    try {
      return window[storageName].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(storageName, key, value) {
    try {
      window[storageName].setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  var shownKey = 'pgk-lc:shown:' + config.launchAt;
  var lastKey = 'pgk-lc:last:' + config.launchAt;

  // "Once per session" cannot be honoured without sessionStorage, so the popup is skipped when it is blocked.
  function sessionUsable() {
    return storageSet('sessionStorage', 'pgk-lc:probe', '1');
  }

  // In the theme editor the preview reloads on every setting change, so the once-per-session rule is skipped there
  var inEditor = (window.Shopify || {}).designMode === true;

  function alreadyShown() {
    return !inEditor && !!storageGet('sessionStorage', shownKey);
  }

  if (launchTime === null || isExcludedPage()) return;
  if (!inEditor && !sessionUsable()) return;
  if (alreadyShown()) return;

  /* ------------------------------------------------------------------------------------------------------------
   * Conflicts with other popups (Klaviyo signup form, theme drawers and modals)
   * ---------------------------------------------------------------------------------------------------------- */

  var klaviyoOpenForms = {};

  // Documented Klaviyo onsite event: https://help.klaviyo.com/hc/en-us/articles/360002035871
  window.addEventListener('klaviyoForms', function (event) {
    var detail = event.detail || {};
    if (detail.type === 'open') {
      klaviyoOpenForms[detail.formId] = true;
      close(false); // never sit on top of, or fight for focus with, the signup form
    } else if (detail.type === 'close') {
      delete klaviyoOpenForms[detail.formId];
    }
  });

  function isVisible(element) {
    return element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden';
  }

  function findBlockers() {
    var blockers = Array.prototype.filter.call(document.querySelectorAll(BLOCKER_SELECTOR), function (element) {
      return !root.contains(element) && isVisible(element);
    });
    // Impact adds "lock" to <html> while a drawer or modal that locks scrolling is open
    if (document.documentElement.classList.contains('lock') || Object.keys(klaviyoOpenForms).length > 0) {
      blockers.push(document.documentElement);
    }
    return blockers;
  }

  var blockerObserver = null;
  var blockerTimer = 0;

  function onBlockerSignal() {
    clearTimeout(blockerTimer);
    blockerTimer = setTimeout(function () {
      stopWatchingBlockers();
      attempt();
    }, 400);
  }

  function watchBlockers(blockers) {
    stopWatchingBlockers();
    blockerObserver = new MutationObserver(onBlockerSignal);
    blockers.forEach(function (element) {
      blockerObserver.observe(element, { attributes: true, attributeFilter: ['open', 'class', 'style', 'hidden', 'aria-hidden'] });
      if (element.parentNode && element.parentNode.nodeType === 1) {
        blockerObserver.observe(element.parentNode, { childList: true });
      }
    });
    blockerObserver.observe(document.body, { childList: true });
    document.addEventListener('dialog:after-hide', onBlockerSignal); // Impact dialogs, drawers and popovers
    window.addEventListener('klaviyoForms', onBlockerSignal);
  }

  function stopWatchingBlockers() {
    clearTimeout(blockerTimer);
    if (blockerObserver) {
      blockerObserver.disconnect();
      blockerObserver = null;
    }
    document.removeEventListener('dialog:after-hide', onBlockerSignal);
    window.removeEventListener('klaviyoForms', onBlockerSignal);
  }

  /* ------------------------------------------------------------------------------------------------------------
   * Eligibility
   * ---------------------------------------------------------------------------------------------------------- */

  var state = 'idle'; // idle → open → closed
  var rechecked = false;
  var assetsReady = false;
  var assetsPromise = null;

  function loadStylesheet(href) {
    return new Promise(function (resolve, reject) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  // The stylesheet and Anton are only requested once the popup is actually about to show
  function loadAssets() {
    if (!assetsPromise) {
      var font = loadStylesheet(FONT_URL)
        .then(function () {
          return document.fonts && document.fonts.load ? document.fonts.load('150px Anton', '0123456789') : null;
        })
        .catch(function () {});
      var fontOrTimeout = Promise.race([font, new Promise(function (resolve) { setTimeout(resolve, 2500); })]);
      assetsPromise = Promise.all([loadStylesheet(config.stylesheet), fontOrTimeout]);
    }
    return assetsPromise;
  }

  function attempt() {
    if (state !== 'idle') return;

    var count = currentCount();
    if (count === null || count < threshold) {
      if (rechecked) return;
      rechecked = true;
      var wait = launchTime + elapsedToReach(Math.max(threshold, INITIAL_COUNT)) - Date.now() + 50;
      if (wait > 0 && wait <= MAX_TIMEOUT) setTimeout(attempt, wait);
      return;
    }

    if (alreadyShown()) return;

    if (document.visibilityState !== 'visible') {
      document.addEventListener('visibilitychange', function onVisible() {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', onVisible);
        attempt();
      });
      return;
    }

    var blockers = findBlockers();
    if (blockers.length > 0) {
      watchBlockers(blockers);
      return;
    }

    if (!assetsReady) {
      loadAssets().then(function () {
        assetsReady = true;
        attempt(); // everything is re-checked: another popup may have opened while the assets were loading
      }, function () {});
      return;
    }

    open(count);
  }

  /* ------------------------------------------------------------------------------------------------------------
   * Split-flap display
   * ---------------------------------------------------------------------------------------------------------- */

  var card = root.querySelector('.pgk-lc__card');
  var clock = root.querySelector('.pgk-lc__clock');
  var staticTop = root.querySelector('[data-pgk-lc-top]');
  var staticBottom = root.querySelector('[data-pgk-lc-bottom]');
  var flapTop = root.querySelector('[data-pgk-lc-flap-top]');
  var flapBottom = root.querySelector('[data-pgk-lc-flap-bottom]');
  var status = root.querySelector('[data-pgk-lc-status]');

  var displayed = null;
  var running = false;
  var reducedMotion = false;
  var animations = [];
  var tickTimer = 0;
  var startTimer = 0;

  function setDigits(value) {
    clock.style.setProperty('--pgk-lc-digits', String(value).length);
  }

  function showStatic(value) {
    setDigits(value);
    staticTop.textContent = staticBottom.textContent = value;
    displayed = value;
  }

  function readLast() {
    var raw = storageGet('localStorage', lastKey);
    return raw !== null && /^\d{1,9}$/.test(raw) && +raw >= INITIAL_COUNT ? +raw : null;
  }

  function announce(value) {
    var text = value + ' ' + config.caption;
    if (status.textContent !== text) status.textContent = text;
  }

  function animateFlap(flap, keyframes, options) {
    flap.hidden = false;
    var animation = flap.animate(keyframes, options);
    animations.push(animation);
    return animation.finished;
  }

  function resetFlaps() {
    animations.forEach(function (animation) { animation.cancel(); });
    animations = [];
    flapTop.hidden = flapBottom.hidden = true;
  }

  // One split-flap step: the old top half falls to the hinge, then the new bottom half drops into place.
  function flip(value) {
    setDigits(value);
    flapTop.firstElementChild.textContent = displayed;
    flapBottom.firstElementChild.textContent = value;
    staticTop.textContent = value;

    return animateFlap(flapTop, [{ transform: 'rotateX(0deg)' }, { transform: 'rotateX(-90deg)' }], { duration: 300, easing: 'ease-in', fill: 'forwards' })
      .then(function () {
        flapTop.hidden = true;
        return animateFlap(
          flapBottom,
          [{ transform: 'rotateX(90deg)' }, { transform: 'rotateX(-7deg)', offset: 0.72 }, { transform: 'rotateX(0deg)' }],
          { duration: 340, easing: 'ease-out' }
        );
      })
      .then(function () {
        staticBottom.textContent = value;
        resetFlaps();
        displayed = value;
        storageSet('localStorage', lastKey, String(value));
      });
  }

  // Brings the display up to the latest projection. Only one sequence runs at a time; it re-reads the projection
  // after every step, so a change that lands mid-sequence is picked up by the same run.
  function sync() {
    if (state !== 'open' || running) return;
    clearTimeout(tickTimer);
    running = true;
    step();
  }

  function step() {
    if (state !== 'open') {
      running = false;
      return;
    }

    var target = currentCount();
    if (reducedMotion || target < displayed) {
      showStatic(target);
      storageSet('localStorage', lastKey, String(target));
    } else if (target - displayed > MAX_ANIMATED_GAP) {
      showStatic(target - MAX_ANIMATED_GAP);
    }

    if (displayed < target) {
      flip(displayed + 1).then(step, function () { running = false; });
      return;
    }

    running = false;
    announce(displayed);

    // Single timeout for the next scheduled change, no polling
    var wait = launchTime + elapsedToReach(displayed + 1) - Date.now() + 20;
    if (wait <= MAX_TIMEOUT) tickTimer = setTimeout(sync, Math.max(wait, 0));
  }

  /* ------------------------------------------------------------------------------------------------------------
   * Dialog
   * ---------------------------------------------------------------------------------------------------------- */

  var previousFocus = null;
  var inertElements = [];
  var savedHtmlStyle = null;

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== 'Tab') return;

    var focusable = root.querySelectorAll('a[href], button:not([disabled])');
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (!root.contains(active) || (event.shiftKey && (active === first || active === card))) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onRootClick(event) {
    if (event.target === root || event.target.closest('[data-pgk-lc-close]')) close(true);
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') sync();
  }

  function lockPage() {
    previousFocus = document.activeElement;

    var html = document.documentElement;
    var scrollbarWidth = window.innerWidth - html.clientWidth;
    savedHtmlStyle = { overflow: html.style.overflow, paddingRight: html.style.paddingRight };
    html.style.overflow = 'hidden';
    if (scrollbarWidth > 0) html.style.paddingRight = scrollbarWidth + 'px';

    inertElements = Array.prototype.filter.call(document.body.children, function (element) {
      return element !== root && !element.inert && !/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(element.tagName);
    });
    inertElements.forEach(function (element) { element.inert = true; });
  }

  function open(count) {
    state = 'open';
    storageSet('sessionStorage', shownKey, '1');
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof card.animate !== 'function';

    // Where the opening sequence starts: one below on a first visit, the last value this browser saw otherwise
    // (at most 10 steps back), or straight on the current value when there is nothing new to flip through.
    var last = readLast();
    var start = count;
    if (!reducedMotion) {
      if (last === null) start = Math.max(INITIAL_COUNT, count - 1);
      else if (last < count) start = Math.max(last, count - MAX_ANIMATED_GAP);
    }
    showStatic(start);
    if (start === count) storageSet('localStorage', lastKey, String(count));
    status.textContent = count + ' ' + config.caption; // read once with the dialog; frames are never announced

    // Inside the theme editor the popup is display-only: it does not lock the page, make it inert or take focus,
    // so it cannot get in the way of the editor driving the preview.
    if (!inEditor) lockPage();

    root.hidden = false;
    root.offsetWidth; // reflow so the fade-in transition runs
    root.classList.add('is-open');
    if (!inEditor) card.focus({ preventScroll: true });

    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('visibilitychange', onVisibilityChange);
    root.addEventListener('click', onRootClick);

    startTimer = setTimeout(sync, reducedMotion ? 0 : 450);
  }

  function close(restoreFocus) {
    if (state !== 'open') return;
    state = 'closed';

    clearTimeout(tickTimer);
    clearTimeout(startTimer);
    resetFlaps();

    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    root.removeEventListener('click', onRootClick);

    inertElements.forEach(function (element) { element.inert = false; });
    inertElements = [];
    if (savedHtmlStyle) {
      document.documentElement.style.overflow = savedHtmlStyle.overflow;
      document.documentElement.style.paddingRight = savedHtmlStyle.paddingRight;
      savedHtmlStyle = null;
    }

    root.classList.remove('is-open');
    setTimeout(function () { root.hidden = true; }, reducedMotion ? 0 : 250);

    if (restoreFocus && previousFocus && typeof previousFocus.focus === 'function' && document.contains(previousFocus)) {
      previousFocus.focus({ preventScroll: true });
    }
  }

  // The delay is measured from page load, not from when this deferred script happened to run
  if (inEditor && document.readyState !== 'complete') {
    // Let the editor finish loading the preview before anything opens on top of it
    window.addEventListener('load', function () {
      setTimeout(attempt, Math.max(500, delay - performance.now()));
    });
  } else {
    setTimeout(attempt, Math.max(0, delay - performance.now()));
  }
})();
